import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Music, 
  User, 
  Album, 
  Clock, 
  TrendingUp, 
  AlertCircle,
  Play,
  Pause,
  Volume2
} from 'lucide-react';

interface VideoSpotifyMetadataProps {
  spotifyTrackId?: string;
  songName?: string;
  artistName?: string;
  albumName?: string;
  albumCoverUrl?: string;
  thumbnailUrl?: string; // Fallback if no album cover
  duration?: number; // Duration in seconds
  spotifyDurationMs?: number;
  spotifyPopularity?: number;
  spotifyExplicit?: boolean;
  spotifyPreviewUrl?: string;
  className?: string;
}

// Format duration from milliseconds or seconds
const formatDuration = (seconds?: number, milliseconds?: number): string => {
  const totalMs = milliseconds || (seconds ? seconds * 1000 : 0);
  if (!totalMs) return '0:00';
  
  const minutes = Math.floor(totalMs / 60000);
  const secs = Math.floor((totalMs % 60000) / 1000);
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

const VideoSpotifyMetadata: React.FC<VideoSpotifyMetadataProps> = ({
  spotifyTrackId,
  songName,
  artistName,
  albumName,
  albumCoverUrl,
  thumbnailUrl,
  duration,
  spotifyDurationMs,
  spotifyPopularity,
  spotifyExplicit,
  spotifyPreviewUrl,
  className = ''
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioRef, setAudioRef] = useState<HTMLAudioElement | null>(null);

  const hasSpotifyData = !!(spotifyTrackId || spotifyPopularity || spotifyPreviewUrl);
  const imageUrl = albumCoverUrl || thumbnailUrl;

  const handleImageLoad = () => setImageLoaded(true);
  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(true);
  };

  const togglePlayback = () => {
    if (!spotifyPreviewUrl || !audioRef) return;

    if (isPlaying) {
      audioRef.pause();
    } else {
      audioRef.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  React.useEffect(() => {
    if (spotifyPreviewUrl) {
      const audio = new Audio(spotifyPreviewUrl);
      audio.addEventListener('ended', handleAudioEnded);
      setAudioRef(audio);

      return () => {
        audio.pause();
        audio.removeEventListener('ended', handleAudioEnded);
      };
    }
  }, [spotifyPreviewUrl]);

  React.useEffect(() => {
    return () => {
      if (audioRef) {
        audioRef.pause();
      }
    };
  }, [audioRef]);

  if (!songName && !artistName && !hasSpotifyData) {
    return null;
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Music className="h-5 w-5" />
          Song Information
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-4">
          {/* Album Art */}
          {imageUrl && (
            <div className="shrink-0 relative">
              {!imageLoaded && (
                <Skeleton className="w-20 h-20 rounded-md" />
              )}
              {!imageError ? (
                <img 
                  src={imageUrl} 
                  alt={`${albumName || songName} cover`}
                  className={`w-20 h-20 rounded-md object-cover shadow-xs ${
                    imageLoaded ? 'block' : 'hidden'
                  }`}
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                />
              ) : (
                <div className="w-20 h-20 rounded-md bg-muted flex items-center justify-center">
                  <Album className="h-8 w-8 text-muted-foreground" />
                </div>
              )}
              
              {/* Preview Play Button Overlay */}
              {spotifyPreviewUrl && imageLoaded && !imageError && (
                <Button
                  size="sm"
                  variant="secondary"
                  className="absolute inset-0 w-full h-full opacity-0 hover:opacity-90 transition-opacity flex items-center justify-center"
                  onClick={togglePlayback}
                >
                  {isPlaying ? (
                    <Pause className="h-8 w-8" />
                  ) : (
                    <Play className="h-8 w-8" />
                  )}
                </Button>
              )}
            </div>
          )}
          
          {/* Song Details */}
          <div className="flex-1 space-y-1">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-medium text-base">{songName || 'Unknown Song'}</p>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {artistName || 'Unknown Artist'}
                </p>
              </div>
              {spotifyExplicit && (
                <Badge variant="secondary" className="text-xs">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Explicit
                </Badge>
              )}
            </div>
            
            {albumName && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Album className="h-3 w-3" />
                {albumName}
              </p>
            )}
            
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {(duration || spotifyDurationMs) && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatDuration(duration, spotifyDurationMs)}
                </span>
              )}
              {spotifyPopularity !== undefined && (
                <span className="flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" />
                  {spotifyPopularity}% popular
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Spotify Preview Controls */}
        {spotifyPreviewUrl && (
          <div className="pt-2 border-t">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={togglePlayback}
            >
              {isPlaying ? (
                <>
                  <Pause className="h-4 w-4 mr-2" />
                  Pause Preview
                </>
              ) : (
                <>
                  <Volume2 className="h-4 w-4 mr-2" />
                  Play 30s Preview
                </>
              )}
            </Button>
          </div>
        )}

        {/* Spotify Attribution */}
        {hasSpotifyData && (
          <div className="pt-2 flex items-center justify-center">
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
              </svg>
              <span>Powered by Spotify</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default VideoSpotifyMetadata; 